/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { throttle } from 'lodash';
import type { Feature, View } from 'ol';
import { getIntersection } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import type { Point } from 'ol/geom';
import { toLonLat } from 'ol/proj';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { dateUtils } from '@opengeoweb/shared';
import type { TimeSliderStartEndStep } from '@opengeoweb/store';
import { layerActions } from '@opengeoweb/store';
import type { Dimension, RefTimeDimensionModes } from '@opengeoweb/webmap';
import { getWMLayerById, LayerType, WMJSDimension } from '@opengeoweb/webmap';
import type {
  MapLocation,
  OnInitializeLayerProps,
  TimeContextType,
  UpdateLayerInfoPayload,
} from '@opengeoweb/webmap-react';
import { makeTimeList } from '@opengeoweb/webmap-react';

export const makePrefetchTimeSpan = (
  timeSliderStartCenterEndTime: TimeSliderStartEndStep,
  isAnimating: boolean,
  prefetchList: number[],
): TimeContextType => {
  const timeSliderTimeStepInS = timeSliderStartCenterEndTime.step * 60 * 1000;
  const prefetchStart = timeSliderStartCenterEndTime.start * 1000;
  const prefetchEnd = timeSliderStartCenterEndTime.end * 1000;

  if (isAnimating || !prefetchStart || !prefetchEnd) {
    return {
      isAnimating,
      prefetchList,
    };
  }

  const roundStart =
    Math.round(prefetchStart / timeSliderTimeStepInS) * timeSliderTimeStepInS;

  const roundEnd =
    Math.round(prefetchEnd / timeSliderTimeStepInS) * timeSliderTimeStepInS +
    timeSliderTimeStepInS;

  const timeSpanList = makeTimeList({
    start: roundStart,
    end: roundEnd,
    step: timeSliderTimeStepInS,
  });

  return {
    isAnimating,
    prefetchList: timeSpanList,
  };
};

export const usePrefetchTimeSpan = (
  timeSliderStartCenterEndTime: TimeSliderStartEndStep,
  isAnimating: boolean,
  animationList: number[],
  refTimeDimensionModes?: RefTimeDimensionModes,
): TimeContextType => {
  const [prefetchTimespan, setPrefetchTimespan] = useState<TimeContextType>({
    isAnimating,
    prefetchList: animationList || [],
  });

  const makeThrottledPrefetchTimespan = useMemo(
    () =>
      throttle(
        (timeSliderStartCenterEndTime, isAnimating, animationList) =>
          setPrefetchTimespan(
            makePrefetchTimeSpan(
              timeSliderStartCenterEndTime,
              isAnimating,
              animationList,
            ),
          ),
        500,
        {
          leading: true,
          trailing: true,
        },
      ),
    [],
  );

  useEffect(
    () =>
      makeThrottledPrefetchTimespan(
        timeSliderStartCenterEndTime,
        isAnimating,
        animationList,
      ),
    [
      animationList,
      isAnimating,
      makeThrottledPrefetchTimespan,
      timeSliderStartCenterEndTime,
    ],
  );
  return { ...prefetchTimespan, refTimeDimensionModes };
};

export const makeLayerInfoPayload = (
  payload: OnInitializeLayerProps,
  mapDimensions: Dimension[],
  mapId: string,
): UpdateLayerInfoPayload => {
  // During intialization, try to base the layer dimension on the current map dimension value.
  const origin = 'onInitializeLayer in OlMapViewLayerConnect';
  const dimensions = (payload.dimensions || []).map((layerDim: Dimension) => {
    const currentMapDim =
      (mapDimensions || []).find((mapDim) => {
        return mapDim.name === layerDim.name;
      })?.currentValue || layerDim.currentValue;
    const wmDim = new WMJSDimension({
      ...layerDim,
      refTimeDimensionMode: getWMLayerById(payload.layerId)
        ?.refTimeDimensionMode,
    });
    return {
      ...layerDim,
      currentValue: wmDim.getClosestValue(currentMapDim, true),
    };
  });

  const updatedPayLoad: UpdateLayerInfoPayload = {
    origin,
    layerDimensions: {
      layerId: payload.layerId,
      origin,
      dimensions,
    },
    mapDimensions: {
      mapId,
      origin,
      dimensions,
    },
  };
  return updatedPayLoad;
};

export const getCenterOfLinkedFeature = (
  feature: Feature | undefined,
  srs: string,
): MapLocation | null => {
  if (feature && feature.getGeometry()?.getType() === 'Point') {
    const projectionCoords = (feature.getGeometry() as Point).getCoordinates();
    const coords = toLonLat(projectionCoords, srs);
    const id = feature?.getId()?.toString() || '';

    return {
      lon: coords[0],
      lat: coords[1],
      id,
      serviceId: feature.getProperties().serviceId,
      collectionId: feature.getProperties().collectionId,
    };
  }
  return null;
};

interface UseFitAndHighlightWarningFeatureOptions {
  view: View | undefined;
  warningDialogFeatures?: {
    id: string;
    originalId?: string;
    geoJSON: GeoJSON.FeatureCollection;
  }[];
  srs: string | undefined;
  mapId: string;
}

export function useFitAndHighlightWarningFeature({
  view,
  warningDialogFeatures,
  srs,
  mapId,
}: UseFitAndHighlightWarningFeatureOptions): void {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!view || !warningDialogFeatures?.length || !srs) {
      return;
    }

    const geoJsonFeature = warningDialogFeatures[0]?.geoJSON?.features?.[0];
    const warningLevel = geoJsonFeature?.properties?.warningLevel;
    const isAviationWarning = warningLevel === 'SIG' || warningLevel === 'AIR';

    if (!geoJsonFeature) {
      return;
    }

    const warningDialogFeature = new GeoJSON().readFeature(geoJsonFeature, {
      featureProjection: srs,
    });

    const geometry = Array.isArray(warningDialogFeature)
      ? warningDialogFeature[0]?.getGeometry()
      : warningDialogFeature.getGeometry();
    if (!geometry) {
      return;
    }

    const featureExtent = geoJsonFeature.bbox ?? geometry.getExtent();
    const currentExtent = view.calculateExtent();

    const intersection = getIntersection(currentExtent, featureExtent);
    const isCompletelyOutside =
      !intersection ||
      intersection[0] >= intersection[2] ||
      intersection[1] >= intersection[3];

    let shouldPan = false;

    if (isCompletelyOutside) {
      shouldPan = true;
    } else {
      const featureArea =
        (featureExtent[2] - featureExtent[0]) *
        (featureExtent[3] - featureExtent[1]);

      const visibleArea =
        (intersection[2] - intersection[0]) *
        (intersection[3] - intersection[1]);

      const visibleRatio = visibleArea / featureArea;
      shouldPan = visibleRatio <= 0.5;
    }

    if (shouldPan) {
      view.fit(featureExtent, {
        duration: 500,
        padding: [40, 40, 40, 40],
        maxZoom: 5,
      });
    }

    if (!isAviationWarning) {
      dispatch(
        layerActions.addLayer({
          mapId,
          layer: {
            geojson: {
              type: 'FeatureCollection',
              features: [geoJsonFeature],
            },
            layerType: LayerType.featureLayer,
          },
          layerId: 'highlight-layer',
          origin: 'OlMapViewConnect',
        }),
      );

      // eslint-disable-next-line consistent-return
      return (): void => {
        dispatch(
          layerActions.layerDelete({
            mapId,
            layerId: 'highlight-layer',
          }),
        );
      };
    }
  }, [view, warningDialogFeatures, srs, dispatch, mapId]);
}

/** Compare value with current time and if difference is more than factor times timeStep returns true */
export const getIsOutdated = (
  compareValue: string,
  timeStep: number,
  factor: number,
): boolean => {
  const currentDate = dateUtils.utc();
  const diff = dateUtils.differenceInMinutes(
    currentDate,
    dateUtils.createDate(compareValue),
  );
  if (diff > timeStep * factor) {
    return true;
  }
  return false;
};
